export const PAYMENT_PROVIDERS = ["stripe", "alipay"] as const;

export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

export const PAYMENT_PRODUCT_TYPES = [
	"organization_credits",
	"organization_plan",
	"dev_plan",
	"chat_plan",
	"wallet_topup",
] as const;

export type PaymentProductType = (typeof PAYMENT_PRODUCT_TYPES)[number];

export const PAYMENT_PROVIDER_LABELS: Record<PaymentProvider, string> = {
	stripe: "Stripe",
	alipay: "Alipay",
};

export interface PaymentProviderCapabilities {
	creditTopUp: boolean;
	automaticRenewal: boolean;
	agreementPreauthorization: boolean;
	subscription: boolean;
	savedPaymentMethod: boolean;
	redirectCheckout: boolean;
}

export interface PaymentProviderPublicConfig {
	id: PaymentProvider;
	label: string;
	enabled: boolean;
	capabilities: PaymentProviderCapabilities;
}

export type PaymentProviderCapability = keyof PaymentProviderCapabilities;

function getConfiguredPublicPaymentProviders(
	env: Record<string, string | undefined>,
): PaymentProvider[] | null {
	const raw =
		env.PUBLIC_PAYMENT_PROVIDERS?.trim() ??
		env.NEXT_PUBLIC_PAYMENT_PROVIDERS?.trim();
	if (!raw) {
		return null;
	}

	const configured = raw
		.split(",")
		.map((value) => value.trim())
		.filter((value): value is PaymentProvider => isPaymentProvider(value));

	return Array.from(new Set(configured));
}

export function isPaymentProviderPubliclyEnabled(
	provider: PaymentProvider,
	env: Record<string, string | undefined>,
): boolean {
	const configured = getConfiguredPublicPaymentProviders(env);
	return configured ? configured.includes(provider) : true;
}

export function isStripePaymentEnabled(
	env: Record<string, string | undefined>,
): boolean {
	return Boolean(
		env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY &&
			(env.STRIPE_SECRET_KEY || env.STRIPE_SECRET_KEY_TEST),
	);
}

export function isAlipayPaymentEnabled(
	env: Record<string, string | undefined>,
): boolean {
	return Boolean(
		env.ALIPAY_APP_ID && env.ALIPAY_PRIVATE_KEY && env.ALIPAY_PUBLIC_KEY,
	);
}

export function isAlipaySubscriptionEnabled(
	env: Record<string, string | undefined>,
): boolean {
	return (
		isAlipayPaymentEnabled(env) &&
		env.ALIPAY_SUBSCRIPTION_ENABLED === "true" &&
		Boolean(
			env.ALIPAY_AGREEMENT_SIGN_SCENE &&
				env.ALIPAY_AGREEMENT_ACCESS_PARAMS_JSON,
		)
	);
}

export function getPaymentProviderPublicConfig(
	env: Record<string, string | undefined>,
): PaymentProviderPublicConfig[] {
	const providers: PaymentProviderPublicConfig[] = [
		{
			id: "stripe",
			label: PAYMENT_PROVIDER_LABELS.stripe,
			enabled: isStripePaymentEnabled(env),
			capabilities: {
				creditTopUp: true,
				automaticRenewal: true,
				agreementPreauthorization: false,
				subscription: true,
				savedPaymentMethod: true,
				redirectCheckout: true,
			},
		},
		{
			id: "alipay",
			label: PAYMENT_PROVIDER_LABELS.alipay,
			enabled: isAlipayPaymentEnabled(env),
			capabilities: {
				creditTopUp: true,
				automaticRenewal: isAlipaySubscriptionEnabled(env),
				agreementPreauthorization: isAlipaySubscriptionEnabled(env),
				subscription: isAlipaySubscriptionEnabled(env),
				savedPaymentMethod: false,
				redirectCheckout: true,
			},
		},
	];

	return providers.filter((provider) =>
		isPaymentProviderPubliclyEnabled(provider.id, env),
	);
}

export function findPaymentProviderPublicConfig(
	providers: readonly PaymentProviderPublicConfig[] | null | undefined,
	provider: PaymentProvider,
): PaymentProviderPublicConfig | null {
	return providers?.find((candidate) => candidate.id === provider) ?? null;
}

export function hasEnabledPaymentProviderCapability(
	provider:
		| Pick<PaymentProviderPublicConfig, "enabled" | "capabilities">
		| null
		| undefined,
	capability: PaymentProviderCapability,
): boolean {
	return Boolean(provider?.enabled && provider.capabilities[capability]);
}

export function isPaymentProviderCapabilityEnabled(
	providers: readonly PaymentProviderPublicConfig[] | null | undefined,
	provider: PaymentProvider,
	capability: PaymentProviderCapability,
): boolean {
	return hasEnabledPaymentProviderCapability(
		findPaymentProviderPublicConfig(providers, provider),
		capability,
	);
}

export function getDefaultPaymentProvider(
	env: Record<string, string | undefined>,
	capability?: PaymentProviderCapability,
): PaymentProvider | null {
	const provider = getPaymentProviderPublicConfig(env).find(
		(candidate) =>
			candidate.enabled &&
			(!capability || candidate.capabilities[capability] === true),
	);

	return provider?.id ?? null;
}

export function isPaymentProvider(value: string): value is PaymentProvider {
	return PAYMENT_PROVIDERS.includes(value as PaymentProvider);
}
